import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import CfLoader from "../../common/CfLoader/CfLoader";
import VerificationNavbar from "../CombinedValidate/VerificationNavbar";
import ProgressStepper from "../CombinedValidate/ProgressStepper";
import { fileToBase64 } from "../../utils/fileToBase64";
import { useTranslation } from "react-i18next";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";

const OcrProcessing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { t } = useTranslation();

  const [ocrError, setOcrError] = useState(null);
  const [activeSection, setActiveSection] = useState("vc");
  const [currentStep, setCurrentStep] = useState(0);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [pdfPageCount, setPdfPageCount] = useState(1);

  const steps = [
    t("verify:ocr"),
    t("verify:form"),
    t("verify:payment"),
    t("verify:summary"),
  ];

  useEffect(() => {
    const callOcrApi = async () => {
      try {
        if (!state?.uploadedFile) {
          throw new Error(t("verify:no_file_ocr_processing"));
        }

        setOcrError(null);

        if (state.uploadedImageSrc) {
          setDocumentPreview(state.uploadedImageSrc);
          setIsImageLoading(false);
        }

        let documentBase64;
        try {
          documentBase64 = await fileToBase64(state.uploadedFile);
        } catch (conversionError) {
          console.error("Error converting file to base64:", conversionError);
          documentBase64 = state.uploadedImageSrc;
        }

        if (!documentBase64) {
          throw new Error(t("verify:failed_prepare_ocr_doc"));
        }

        const payload = {
          document: documentBase64,
        };

        const response = await POST(
          `${EntityConstants.POST_OCR_DOCUMENT}`,
          payload,
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message ||
              `${t("verify:api_error")} ${response.statusText}`,
          );
        }

        const responseData = await response.json();

        if (!responseData) {
          throw new Error(t("verify:empty_ocr_response"));
        }

        const extractedData = responseData.data || responseData;
        const s3Url = responseData.s3_file_url || null;
        const originalFileUrl = responseData.file_url || null;
        const programmeDetails = responseData.programme_details || null;
        const orgDetails = responseData.org_details || null;

        if (Object.keys(extractedData).length === 0) {
          throw new Error(t("verify:empty_ocr_data"));
        }

        setTimeout(() => {
          navigateToForm(extractedData, s3Url, originalFileUrl, programmeDetails, orgDetails);
        }, 2000);
      } catch (error) {
        console.error("OCR API error:", error);
        setOcrError(error.message);
        toast.error(`${t("verify:ocr_failed_colon")} ${error.message}`);

        setTimeout(() => {
          handleOcrFailure();
        }, 3000);
      }
    };

    callOcrApi();
  }, [state]);

  const navigateToForm = (responseData, s3Url, originalFileUrl, programmeDetails, orgDetails) => {
    navigate("/verifier/ocr/form", {
      state: {
        ocrData: responseData,
        uploadedDocument: state?.uploadedImageSrc,
        uploadedFileType: state?.uploadedFileType,
        s3FileUrl: s3Url,
        originalFileUrl: originalFileUrl,
        flowType: "ocr",
        currentStep: 1,
        programmeDetails: programmeDetails,
        orgDetails: orgDetails,
      },
    });
  };

  const handleOcrFailure = () => {
    toast.error(t("verify:ocr_failed_manual_redirect_msg"));

    navigate("/verifier/legacy/certificate-details", {
      state: {
        uploadedDocument: state?.uploadedImageSrc,
        uploadedFileType: state?.uploadedFileType,
        flowType: "manual",
      },
    });
  };

  const handleSectionChange = (section) => {
    if (section === "vc") {
      navigate("/verifier/validate");
    }
  };

  const isPdfDocument = state?.uploadedFileType === "application/pdf";

  return (
    <div className="min-h-screen py-[6rem] bg-white px-4">
      <VerificationNavbar
        activeSection={activeSection}
        handleSectionChange={handleSectionChange}
      />
      <ProgressStepper steps={steps} currentStep={currentStep} />

      <div className="w-full mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden backdrop-blur-sm">
          {ocrError ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-red-100 rounded-full blur-2xl opacity-50 animate-pulse" />
                <div className="relative text-red-500">
                  <svg
                    className="w-20 h-20 mx-auto animate-[wiggle_1s_ease-in-out]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{
                      animation: "wiggle 1s ease-in-out",
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">
                {t("verify:ocr_processing_failed")}
              </h2>

              <div className="max-w-md text-center space-y-3">
                <p className="text-slate-600 leading-relaxed">{ocrError}</p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
                  <p className="text-amber-900 text-sm font-medium flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {t("verify:redirecting_manual_verification")}
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <CfLoader />
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 py-12 px-6 lg:px-12">
              <div className="w-full lg:w-1/2">
                <div className="relative">
                  {!isPdfDocument && isImageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-50 rounded-2xl">
                      <CfLoader />
                    </div>
                  )}

                  {documentPreview && (
                    <div className="relative group">
                      <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-slate-200 bg-white">
                        {isPdfDocument ? (
                          <TwoPagePdfViewer
                            pdfUrl={documentPreview}
                            showZoom={false}
                            heightClassName="h-[600px]"
                            onPagesRendered={(count) => setPdfPageCount(count)}
                          />
                        ) : (
                          <img
                            src={documentPreview}
                            alt="Processing document"
                            className={`w-full transition-opacity duration-300 ${
                              isImageLoading ? "opacity-0" : "opacity-100"
                            }`}
                            style={{
                              maxHeight: "600px",
                              objectFit: "contain",
                            }}
                            onLoad={() => setIsImageLoading(false)}
                          />
                        )}

                        <div className="absolute inset-0 pointer-events-none">
                          <div
                            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent shadow-[0_0_20px_rgba(59,130,246,0.8)]"
                            style={{
                              animation:
                                "scanPrimary 3s cubic-bezier(0.4, 0, 0.2, 1) infinite",
                              filter: "blur(1px)",
                            }}
                          />

                          <div
                            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent shadow-[0_0_20px_rgba(59,130,246,0.8)]"
                            style={{
                              animation:
                                "scanPrimary 3s cubic-bezier(0.4, 0, 0.2, 1) infinite",
                              filter: "blur(1px)",
                            }}
                          />

                          <div
                            className="absolute inset-0 opacity-20"
                            style={{
                              backgroundImage:
                                "linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)",
                              backgroundSize: "20px 20px",
                              animation: "gridPulse 2s ease-in-out infinite",
                            }}
                          />

                          <div className="absolute top-4 left-4 w-12 h-12">
                            <div
                              className="absolute inset-0 border-t-4 border-l-4 border-cfPrimary rounded-tl-lg"
                              style={{
                                animation:
                                  "cornerPulse 2s ease-in-out infinite",
                              }}
                            />
                          </div>
                          <div className="absolute top-4 right-4 w-12 h-12">
                            <div
                              className="absolute inset-0 border-t-4 border-r-4 border-cfPrimary rounded-tr-lg"
                              style={{
                                animation:
                                  "cornerPulse 2s ease-in-out infinite 0.5s",
                              }}
                            />
                          </div>
                          <div className="absolute bottom-4 left-4 w-12 h-12">
                            <div
                              className="absolute inset-0 border-b-4 border-l-4 border-cfPrimary rounded-bl-lg"
                              style={{
                                animation:
                                  "cornerPulse 2s ease-in-out infinite 1s",
                              }}
                            />
                          </div>
                          <div className="absolute bottom-4 right-4 w-12 h-12">
                            <div
                              className="absolute inset-0 border-b-4 border-r-4 border-cfPrimary rounded-br-lg"
                              style={{
                                animation:
                                  "cornerPulse 2s ease-in-out infinite 1.5s",
                              }}
                            />
                          </div>

                          <div
                            className="absolute top-1/4 left-1/4 w-2 h-2 bg-green-400 rounded-full shadow-[0_0_8px_rgba(74,222,128,0.8)]"
                            style={{
                              animation: "dataPoint 3s ease-in-out infinite",
                            }}
                          />
                          <div
                            className="absolute top-1/3 right-1/3 w-2 h-2 bg-green-400 rounded-full shadow-[0_0_8px_rgba(74,222,128,0.8)]"
                            style={{
                              animation:
                                "dataPoint 3s ease-in-out infinite 0.5s",
                            }}
                          />
                          <div
                            className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-green-400 rounded-full shadow-[0_0_8px_rgba(74,222,128,0.8)]"
                            style={{
                              animation: "dataPoint 3s ease-in-out infinite 1s",
                            }}
                          />
                          <div
                            className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-green-400 rounded-full shadow-[0_0_8px_rgba(74,222,128,0.8)]"
                            style={{
                              animation:
                                "dataPoint 3s ease-in-out infinite 1.5s",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Processing Info Section */}
              <div className="w-full lg:w-1/2 flex flex-col items-center justify-center">
                <div className="text-center">
                  <div className="mb-6">
                    <div className="w-16 h-16 mx-auto">
                      <svg
                        className="w-full h-full animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        {/* Light ring */}
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="#EAEBFF"
                          strokeWidth="4"
                        ></circle>

                        {/* Dark rotating arc */}
                        <path
                          fill="var(--cf-primary-color)"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  </div>

                  <h2 className="text-3xl font-bold text-gray-800 mb-3">
                    {t("verify:processing_document")}
                  </h2>

                  <p className="text-gray-600 text-lg mb-4">
                    {t("verify:ocr_tech_explanation")}
                  </p>

                  <div className="bg-[#EAEBFF] border-none rounded-xl p-6 mb-6 text-center">
                    <p className="text-gray-900 text-sm">
                      <strong className="block text-base mb-3 font-semibold text-blue-900">
                        {t("verify:whats_happening")}
                      </strong>
                      <div className="flex flex-col justify-center items-center gap-1 text-gray-900 text-sm">
                        <span>• {t("verify:analyzing_doc_structure")}</span>
                        <span>• {t("verify:extracting_text_data")}</span>
                        <span>• {t("verify:validating_info")}</span>
                        <span>• {t("verify:preparing_form_fields")}</span>
                      </div>
                    </p>
                  </div>

                  <div className="flex gap-2 justify-center mb-4">
                    <div className="flex gap-2 justify-center lg:justify-start">
                      <div
                        className="w-3 h-3 bg-cfPrimary rounded-full"
                        style={{
                          animation: "bounce 1.4s ease-in-out infinite",
                        }}
                      />
                      <div
                        className="w-3 h-3 bg-cfPrimary rounded-full"
                        style={{
                          animation: "bounce 1.4s ease-in-out 0.2s infinite",
                        }}
                      />
                      <div
                        className="w-3 h-3 bg-cfPrimary rounded-full"
                        style={{
                          animation: "bounce 1.4s ease-in-out 0.4s infinite",
                        }}
                      />
                    </div>
                  </div>

                  {/* Loading Dots */}

                  <p className="text-sm text-slate-500 text-center lg:text-left font-medium">
                    {t("verify:takes_few_seconds")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes scanPrimary {
          0%, 100% { top: 0%; opacity: 0; }
          5% { opacity: 1; }
          50% { top: 100%; opacity: 1; }
          55%, 100% { top: 100%; opacity: 0; }
        }
        
        @keyframes scanSecondary {
          0%, 100% { top: 0%; opacity: 0; }
          5% { opacity: 0.6; }
          50% { top: 100%; opacity: 0.6; }
          55%, 100% { top: 100%; opacity: 0; }
        }
        
        @keyframes gridPulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.3; }
        }
        
        @keyframes cornerPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.95); }
        }
        
        @keyframes dataPoint {
          0%, 100% { opacity: 0; transform: scale(0); }
          20%, 80% { opacity: 1; transform: scale(1); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-3deg); }
          75% { transform: rotate(3deg); }
        }
      `}</style>
    </div>
  );
};

export default OcrProcessing;
